import React, { useEffect, useRef, useState } from "react";
import "./ProfileApp.css";
import { ApiPaths } from "./../../Config/ApiPath";
import useAxiosHelper from "./../../Common/AxiosHelper";
import { useDispatch, useSelector } from "react-redux";
import { setUserPersonalInfo } from "./../../Redux/ProfileDataSlice";
import { toastSuccess, toastFailed } from "./../../Config/BasicInfo";
import {
  Wallet,
  Mail,
  CalendarDays,
  CalendarCheck,
  Users,
  UserCheck,
  Hash,
  AtSign,
  Phone,
  Copy,
  Check,
  ShieldCheck,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  ChevronRight,
  Share2,
  Link2,
  Plus,
  X,
  AlertTriangle,
  Gift,
} from "lucide-react";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initials = (name, username) =>
  (name || username || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

const shorten = (str, head = 8, tail = 6) => {
  if (!str) return "";
  if (str.length <= head + tail + 3) return str;
  return `${str.slice(0, head)}…${str.slice(-tail)}`;
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";

const PwField = ({ id, label, value, onChange, show, onToggle, placeholder, autoComplete, disabled }) => (
  <label className="prField" htmlFor={id}>
    <span>{label}</span>
    <div className="prPwWrap">
      <Lock size={16} className="prPwLead" />
      <input
        id={id}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        autoComplete={autoComplete}
      />
      <button type="button" onClick={onToggle} aria-label={show ? "Hide password" : "Show password"} tabIndex={-1}>
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </label>
);

const Profile = () => {
  const dispatch = useDispatch();
  const { AxiosGet, AxiosPost } = useAxiosHelper();
  const profileData = useSelector((state) => state.profileData.userPersonalInfo);

  const [copiedKey, setCopiedKey] = useState("");

  // add email
  const [emailSheet, setEmailSheet] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const otpRefs = useRef([]);

  // change password
  const [pwSheet, setPwSheet] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const hasExistingEmail = !!profileData?.email?.trim();
  const isActive = profileData?.status == 1;
  const referralLink = `${window.location.origin}/register?ref=${profileData?.username || ""}`;

  const fetchProfileData = async () => {
    try {
      const response = await AxiosGet(ApiPaths.getProfile);
      dispatch(setUserPersonalInfo(response));
    } catch (error) {
      console.error("Error fetching profile data", error);
    }
  };

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sheets: Escape closes, page behind stays put
  const sheetOpen = emailSheet || pwSheet;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !otpLoading && !passwordLoading) {
        setEmailSheet(false);
        setPwSheet(false);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen, otpLoading, passwordLoading]);

  useEffect(() => {
    if (showOtp) setTimeout(() => otpRefs.current[0]?.focus(), 150);
  }, [showOtp]);

  const copyValue = async (value, key) => {
    if (!value) return;
    const text = String(value);
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // clipboard API blocked (unfocused page / older webviews) — use execCommand
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) {
        toastFailed("Failed to copy");
        return;
      }
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 1500);
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join me on Aethrx", text: "Join Aethrx with my referral link", url: referralLink });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    copyValue(referralLink, "ref");
  };

  /* ---------- add email ---------- */
  const closeEmailSheet = () => {
    setEmailSheet(false);
    setShowOtp(false);
    setOtp(Array(6).fill(""));
    setEmailError("");
    setOtpError("");
  };

  const handleSendOtp = async () => {
    setEmailError("");
    const trimmedEmail = email?.trim();
    if (!trimmedEmail) {
      setEmailError("Email is required");
      return;
    }
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    try {
      setSendOtpLoading(true);
      await AxiosPost(ApiPaths.sendOTP, { action: "email", email: trimmedEmail });
      toastSuccess("OTP sent to your email");
      setOtp(Array(6).fill(""));
      setShowOtp(true);
    } catch (e) {
      setEmailError(e?.response?.data?.message || e?.message || "Failed to send OTP");
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    setOtpError("");
    const code = otp.join("");
    if (code.length !== 6) {
      setOtpError("Enter the 6-digit code");
      return;
    }
    try {
      setOtpLoading(true);
      // update-profile requires name, email and mobile together
      await AxiosPost(ApiPaths.updateProfile, {
        name: profileData?.name,
        mobile: profileData?.mobile,
        email: email?.trim(),
        action: "email",
        otp: code,
      });
      toastSuccess("Email updated successfully");
      setEmail("");
      closeEmailSheet();
      fetchProfileData();
    } catch (e) {
      setOtpError(e?.response?.data?.message || e?.message || "Failed to update email");
    } finally {
      setOtpLoading(false);
    }
  };

  const setDigit = (i, v) => {
    setOtpError("");
    const clean = v.replace(/\D/g, "");
    setOtp((d) => {
      const next = [...d];
      if (!clean) {
        next[i] = "";
        return next;
      }
      clean
        .slice(0, 6 - i)
        .split("")
        .forEach((ch, k) => {
          next[i + k] = ch;
        });
      return next;
    });
    if (clean) otpRefs.current[Math.min(i + clean.length, 5)]?.focus();
  };

  /* ---------- change password ---------- */
  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setShowCurrentPw(false);
    setShowNewPw(false);
    setShowConfirmPw(false);
  };

  const handleChangePassword = async () => {
    setPasswordError("");
    if (!currentPassword?.trim()) return setPasswordError("Current password is required");
    if (!newPassword?.trim()) return setPasswordError("New password is required");
    if (newPassword.length < 4) return setPasswordError("New password must be at least 4 characters");
    if (newPassword !== confirmPassword) return setPasswordError("New password and confirm password do not match");
    if (currentPassword === newPassword) return setPasswordError("New password must be different from current password");

    try {
      setPasswordLoading(true);
      const res = await AxiosPost(ApiPaths.changePassword, { currentPassword, newPassword });
      toastSuccess(res?.message || "Password updated successfully.");
      resetPasswordForm();
      setPwSheet(false);
    } catch (e) {
      const data = e?.response?.data;
      setPasswordError(data?.isStrongPassword?.message || data?.message || e?.message || "Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const CopyBtn = ({ value, k, label }) =>
    value ? (
      <button
        type="button"
        className={`prCopy ${copiedKey === k ? "isDone" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          copyValue(value, k);
        }}
        aria-label={`Copy ${label}`}
      >
        {copiedKey === k ? <Check size={15} /> : <Copy size={15} />}
      </button>
    ) : null;

  const Row = ({ Icon, label, value, muted, children, onClick, tone }) => (
    <div
      className={`prRow ${onClick ? "isTap" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      <span className={`prRowIcon ${tone || ""}`}>
        <Icon size={17} />
      </span>
      <span className="prRowText">
        <small>{label}</small>
        <b className={muted ? "muted" : ""}>{value}</b>
      </span>
      {children}
    </div>
  );

  return (
    <section className="prPage">
      {/* ---------- Hero ---------- */}
      <div className="prHero">
        <div className="prAvatar">
          {initials(profileData?.name, profileData?.username)}
          <i className={isActive ? "on" : "off"} />
        </div>
        <h1>{profileData?.name || "User"}</h1>
        <button type="button" className="prHandle" onClick={() => copyValue(profileData?.username, "user")}>
          @{profileData?.username || "—"}
          {copiedKey === "user" ? <Check size={13} /> : <Copy size={13} />}
        </button>
        <div className="prBadges">
          <span className={`prBadge ${isActive ? "on" : "off"}`}>
            <span className="dot" /> {isActive ? "Active" : "Inactive"}
          </span>
          <span className="prBadge">
            <Hash size={12} /> UID {profileData?.uid ?? "—"}
          </span>
          {profileData?.joining_date && (
            <span className="prBadge">
              <CalendarDays size={12} /> Since {monthYear(profileData.joining_date)}
            </span>
          )}
        </div>
      </div>

      {/* ---------- Referral ---------- */}
      <div className="prReferral">
        <div className="prRefTop">
          <span className="prRefIcon">
            <Gift size={18} />
          </span>
          <div>
            <b>Invite friends</b>
            <small>Share your link — sign-ups join your team.</small>
          </div>
        </div>
        <div className="prRefLink">
          <Link2 size={14} />
          <span title={referralLink}>{referralLink}</span>
        </div>
        <div className="prRefActions">
          <button type="button" className={`prBtn ghost ${copiedKey === "ref" ? "isDone" : ""}`} onClick={() => copyValue(referralLink, "ref")}>
            {copiedKey === "ref" ? <Check size={16} /> : <Copy size={16} />}
            {copiedKey === "ref" ? "Copied" : "Copy link"}
          </button>
          <button type="button" className="prBtn primary" onClick={shareReferral}>
            <Share2 size={16} /> Share
          </button>
        </div>
      </div>

      {/* ---------- Account ---------- */}
      <div className="prGroup">
        <h2>Account</h2>
        <div className="prList">
          <Row Icon={AtSign} label="Username" value={profileData?.username || "—"}>
            <CopyBtn value={profileData?.username} k="user2" label="username" />
          </Row>
          {hasExistingEmail ? (
            <Row Icon={Mail} label="Email" value={profileData?.email} />
          ) : (
            <Row Icon={Mail} label="Email" value="Add your email" muted onClick={() => setEmailSheet(true)} tone="warn">
              <span className="prAdd">
                <Plus size={14} /> Add
              </span>
            </Row>
          )}
          <Row Icon={Phone} label="Mobile" value={profileData?.mobile || "—"} muted={!profileData?.mobile} />
          <Row
            Icon={Wallet}
            label="Wallet address"
            value={profileData?.wallet_address ? shorten(profileData.wallet_address, 10, 8) : "Not linked"}
            muted={!profileData?.wallet_address}
          >
            <CopyBtn value={profileData?.wallet_address} k="wallet" label="wallet address" />
          </Row>
        </div>
      </div>

      {/* ---------- Sponsor ---------- */}
      <div className="prGroup">
        <h2>Sponsor</h2>
        <div className="prList">
          <Row Icon={UserCheck} label="Sponsor ID" value={profileData?.sponsor_username || "—"} muted={!profileData?.sponsor_username}>
            <CopyBtn value={profileData?.sponsor_username} k="sponsor" label="sponsor ID" />
          </Row>
          <Row Icon={Users} label="Sponsor name" value={profileData?.sponsor_name || "—"} muted={!profileData?.sponsor_name} />
        </div>
      </div>

      {/* ---------- Membership ---------- */}
      <div className="prGroup">
        <h2>Membership</h2>
        <div className="prList">
          <Row Icon={CalendarDays} label="Joined" value={formatDate(profileData?.joining_date)} />
          <Row
            Icon={CalendarCheck}
            label="Activated"
            value={isActive ? formatDate(profileData?.Activation_date) : "Not activated yet"}
            muted={!isActive}
            tone={isActive ? "ok" : ""}
          />
        </div>
      </div>

      {/* ---------- Security ---------- */}
      <div className="prGroup">
        <h2>Security</h2>
        <div className="prList">
          <Row Icon={KeyRound} label="Password" value="Change password" onClick={() => setPwSheet(true)}>
            <ChevronRight size={18} className="prChev" />
          </Row>
        </div>
      </div>

      {/* ---------- Change password sheet ---------- */}
      {pwSheet && (
        <div className="prOverlay" onClick={() => !passwordLoading && setPwSheet(false)}>
          <div className="prSheet" role="dialog" aria-modal="true" aria-labelledby="prPwTitle" onClick={(e) => e.stopPropagation()}>
            <span className="prSheetHandle" aria-hidden="true" />
            <button type="button" className="prClose" onClick={() => setPwSheet(false)} disabled={passwordLoading} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="prPwTitle">Change password</h3>
            <p className="prSheetSub">Use a strong password you don't use anywhere else.</p>
            <PwField
              id="currentPassword"
              label="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              show={showCurrentPw}
              onToggle={() => setShowCurrentPw((v) => !v)}
              placeholder="Enter current password"
              autoComplete="current-password"
              disabled={passwordLoading}
            />
            <PwField
              id="newPassword"
              label="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              show={showNewPw}
              onToggle={() => setShowNewPw((v) => !v)}
              placeholder="Enter new password"
              autoComplete="new-password"
              disabled={passwordLoading}
            />
            <PwField
              id="confirmPassword"
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              show={showConfirmPw}
              onToggle={() => setShowConfirmPw((v) => !v)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
              disabled={passwordLoading}
            />
            {confirmPassword && newPassword !== confirmPassword && !passwordError && (
              <p className="prHint bad">Passwords don't match yet</p>
            )}
            {passwordError && (
              <p className="prError">
                <AlertTriangle size={14} /> {passwordError}
              </p>
            )}
            <button type="button" className="prSubmit" onClick={handleChangePassword} disabled={passwordLoading}>
              {passwordLoading ? <span className="prSpin" /> : <ShieldCheck size={18} />}
              {passwordLoading ? "Updating…" : "Update password"}
            </button>
          </div>
        </div>
      )}

      {/* ---------- Add email sheet ---------- */}
      {emailSheet && (
        <div className="prOverlay" onClick={() => !otpLoading && closeEmailSheet()}>
          <div className="prSheet" role="dialog" aria-modal="true" aria-labelledby="prEmailTitle" onClick={(e) => e.stopPropagation()}>
            <span className="prSheetHandle" aria-hidden="true" />
            <button type="button" className="prClose" onClick={closeEmailSheet} disabled={otpLoading} aria-label="Close">
              <X size={18} />
            </button>
            {!showOtp ? (
              <>
                <h3 id="prEmailTitle">Add email</h3>
                <p className="prSheetSub">We'll send a verification code to confirm it.</p>
                <label className="prField">
                  <span>Email address</span>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmailError("");
                      setEmail(e.target.value);
                    }}
                    disabled={sendOtpLoading}
                  />
                </label>
                {emailError && (
                  <p className="prError">
                    <AlertTriangle size={14} /> {emailError}
                  </p>
                )}
                <button type="button" className="prSubmit" onClick={handleSendOtp} disabled={sendOtpLoading}>
                  {sendOtpLoading ? <span className="prSpin" /> : <Mail size={18} />}
                  {sendOtpLoading ? "Sending…" : "Send code"}
                </button>
              </>
            ) : (
              <>
                <h3 id="prEmailTitle">Verify email</h3>
                <p className="prSheetSub">
                  Enter the 6-digit code sent to <b>{email}</b>.
                </p>
                <div className="prOtp" role="group" aria-label="Verification code">
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      className={`${d ? "isFilled" : ""} ${otpError ? "isError" : ""}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      maxLength={6}
                      value={d}
                      aria-label={`Digit ${i + 1}`}
                      onChange={(e) => setDigit(i, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
                        if (e.key === "Enter") handleOtpSubmit();
                      }}
                      onFocus={(e) => e.target.select()}
                    />
                  ))}
                </div>
                {otpError && (
                  <p className="prError center">
                    <AlertTriangle size={14} /> {otpError}
                  </p>
                )}
                <button type="button" className="prSubmit" onClick={handleOtpSubmit} disabled={otpLoading}>
                  {otpLoading ? <span className="prSpin" /> : <ShieldCheck size={18} />}
                  {otpLoading ? "Verifying…" : "Verify & save"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

// "Oct 2026" for the hero badge
function monthYear(d) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export default Profile;
